/**
 * Preferences that belong to the person, not to the shell they happen to be in.
 *
 * THE PROBLEM IS THE SAME ONE THE SESSION HAD. `localStorage` is scoped to an
 * ORIGIN, and there are now four of them — app / brainstorm / beats / publish.
 * A preference stored there follows the hostname, not you: pick dark mode on
 * brainstorm.lumnian.com, open beats.lumnian.com, and it is gone. It read as a
 * theme bug, but nothing was ever wrong with the theme — it was written to a
 * drawer the other shells cannot open.
 *
 * The theme hid this well, because `initialTheme()` falls back to
 * `prefers-color-scheme`. If your OS is dark, every shell looks right anyway;
 * the split only shows when your explicit choice DISAGREES with your OS, which
 * is exactly the person who cared enough to set it.
 *
 * So: a cookie on the parent domain, the same trick that made one login work
 * everywhere. Not HttpOnly — script has to read this, and unlike the session it
 * is a UI preference, so there is nothing here worth stealing.
 *
 * NOT EVERYTHING BELONGS HERE. Nav memory, the sibling-pages sidebar, the
 * subject rail, the link-builder drop mode and the brainstorm facet panel are
 * all state about ONE product's screens, and each only exists in the shell that
 * owns it. Per-origin is correct for those. This module is for the short list
 * that is genuinely app-wide: the theme, and the internals reveal.
 *
 * Pure and dependency-free so CRA's jest can test it.
 */

/** A year. Long enough that a preference outlives any session. */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * The domain to scope a shared cookie to, or null to leave it host-only.
 *
 * `beats.lumnian.com` -> `.lumnian.com`, so every shell reads the same value.
 *
 * THREE LABELS MINIMUM, and that guard is load-bearing rather than defensive.
 * With two, `lumnian.com` would yield `.com` — a public suffix, which every
 * browser rejects outright, so the write would silently do nothing. Falling back
 * to host-only is right there anyway: a single host has no siblings to share
 * with.
 */
export function cookieDomain(hostname: string): string | null {
  const labels = (hostname || '').split('.');
  if (labels.length < 3) return null;
  // An IP address has no parent domain to share with, and `.0.1` is not a thing.
  if (/^\d+$/.test(labels[labels.length - 1])) return null;
  return `.${labels.slice(1).join('.')}`;
}

/** Read one value out of a `document.cookie` string. */
export function readCookie(cookieString: string, name: string): string | null {
  for (const part of (cookieString || '').split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() !== name) continue;
    const raw = part.slice(eq + 1).trim();
    try {
      return decodeURIComponent(raw);
    } catch {
      // A value that predates encoding, or someone else's malformed cookie.
      return raw;
    }
  }
  return null;
}

/** The `document.cookie` assignment that stores `value` for every shell. */
export function serializePref(
  name: string,
  value: string,
  hostname: string,
  protocol: string,
): string {
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'path=/',
    `max-age=${MAX_AGE_SECONDS}`,
    'samesite=lax',
  ];
  const domain = cookieDomain(hostname);
  if (domain) parts.push(`domain=${domain}`);
  // Only over https. Marking it Secure on local http would make the browser
  // drop it, which is how development ends up "fixed" and production broken.
  if (protocol === 'https:') parts.push('secure');
  return parts.join('; ');
}

/** The assignment that deletes it.
 *
 *  The domain and path MUST match the ones it was written with or the browser
 *  keeps the original — the same footgun `clear_session_cookie` documents on the
 *  backend, where a mismatched delete made logging out do nothing. */
export function serializeRemoval(name: string, hostname: string): string {
  const parts = [`${name}=`, 'path=/', 'max-age=0', 'samesite=lax'];
  const domain = cookieDomain(hostname);
  if (domain) parts.push(`domain=${domain}`);
  return parts.join('; ');
}

// --- the impure edge ---------------------------------------------------------
//
// Everything above is pure and tested. These three touch `document` and
// `window`, and every access is wrapped: cookies can be blocked outright and
// `localStorage` throws in private modes. A preference is never worth a crash,
// so each failure degrades to today's per-shell behaviour rather than throwing.

export function readPref(name: string): string | null {
  try {
    const shared = readCookie(document.cookie, name);
    if (shared !== null) return shared;
  } catch {
    // fall through
  }
  // A CHOICE MADE BEFORE THIS EXISTED STILL COUNTS. Without this read, shipping
  // the cookie would reset everyone's theme to their OS default, and the change
  // meant to make the preference follow you would start by throwing it away.
  try {
    return localStorage.getItem(name);
  } catch {
    return null;
  }
}

export function writePref(name: string, value: string): void {
  try {
    document.cookie = serializePref(
      name,
      value,
      window.location.hostname,
      window.location.protocol,
    );
  } catch {
    // fall through to localStorage
  }
  // Mirrored, deliberately. The cookie is read first so this is never the source
  // of truth — it is what keeps the app working for someone whose browser blocks
  // cookies, and it keeps a shell still serving an older bundle in agreement.
  try {
    localStorage.setItem(name, value);
  } catch {
    // best-effort persistence
  }
}

export function clearPref(name: string): void {
  try {
    document.cookie = serializeRemoval(name, window.location.hostname);
  } catch {
    // fall through
  }
  try {
    localStorage.removeItem(name);
  } catch {
    // best-effort
  }
}
